import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { Box, Typography } from '@mui/material';
import type { Financial } from '../../types';
import { useTranslation } from 'react-i18next';

interface FinancialChartProps {
  data: Financial;
}

export const FinancialChart: React.FC<FinancialChartProps> = ({ data }) => {
  const { t } = useTranslation();

  // Calculate the maximum value from the sum of both series
  const maxValue = Math.max(
    ...data.rent.map((val, index) => (val + data.charges[index]) / 1000)
  ).toFixed(1);

  // Calculate the appropriate max for y-axis
  const calculateYAxisMax = (maxVal: number) => {
    // If value is very small, use a lower base
    if (maxVal <= 50) return Math.ceil(maxVal / 10) * 10;
    if (maxVal <= 100) return Math.ceil(maxVal / 25) * 25;
    if (maxVal <= 250) return Math.ceil(maxVal / 50) * 50;

    // Round up to the next 50k increment
    return Math.ceil(maxVal / 50) * 50;
  };

  // Calculate tick amount based on the max value
  const calculateTickAmount = (maxVal: number) => {
    if (maxVal <= 250) return 5;
    return Math.min(10, Math.ceil(maxVal / 50));
  };

  const yAxisMax = calculateYAxisMax(maxValue);
  const tickAmount = calculateTickAmount(yAxisMax);

  const options = {
    chart: {
      type: 'bar' as const,
      height: 350,
      stacked: true,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
      fontFamily: 'inherit',
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: data.x_axis.length > 9 ? '90%' : '50%',
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'last',
      },
    },
    dataLabels: {
      enabled: true,
      formatter(val: number, { seriesIndex, dataPointIndex, w }: any) {
        // Show the individual values for each section
        return `${val}k`;
      },
      style: {
        colors: ['#FFFFFF'],
        fontSize: '14px',
        fontWeight: 500,
      },
      textAnchor: 'middle',
      position: 'center',
    },
    annotations: {
      texts: data.x_axis.map((_, index) => ({
        x: index,
        y: data.rent[index] + data.charges[index],
        text: `${((data.rent[index] + data.charges[index]) / 1000).toFixed(1)}k`,
        textAnchor: 'middle',
        fontSize: '14px',
        fontWeight: 500,
        foreColor: '#212529',
        offsetY: -15, // Move the total value up a bit from the bar
      })),
    },
    states: {
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
        },
      },
      hover: {
        filter: {
          type: 'opacity',
          value: 0.75,
        },
      },
    },
    stroke: {
      width: 0,
      colors: ['#000000'],
      show: true,
    },
    xaxis: {
      categories: data.x_axis,
      type: 'category' as const,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: '#495057',
          fontSize: '14px',
          fontWeight: 500,
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (val: number) => `${val}k`,
        style: {
          colors: '#495057',
          fontSize: '14px',
          fontWeight: 500,
        },
        padding: 10,
      },
      min: 0,
      max: yAxisMax,
      tickAmount,
      forceNiceScale: true, // Ensures nice round numbers for ticks
    },
    legend: {
      show: false,
    },
    fill: {
      opacity: 1,
      colors: ['#3C6895', '#1B998B'],
    },
    tooltip: {
      enabled: true,
      shared: true,
      intersect: false,
      onDatasetHover: {
        highlightDataSeries: true,
      },
      custom({ series, dataPointIndex }: any) {
        const total = Number(
          series.reduce((acc: number, curr: number[]) => acc + curr[dataPointIndex], 0).toFixed(1)
        );
        const netRents = Number(series[1][dataPointIndex].toFixed(1));
        const charges = Number(series[0][dataPointIndex].toFixed(1));

        return `
              <div style="
                font-size: 14px;
                font-weight: 700;
                line-height: 21px;
                color: #212529;
                margin-bottom: 2px;
                font-family: 'sofia-pro', sans-serif;
              ">
                Total rents: ${total}k€
              </div>
              <div style="
                display: flex;
                align-items: center;
                line-height: 24px;
                color: #212529;
                font-family: 'sofia-pro', sans-serif;
              ">
                <span style="
                  width: 12px;
                  height: 12px;
                  background: #1B998B;
                  border-radius: 50%;
                  display: inline-block;
                  margin-right: 12px;
                  flex-shrink: 0;
                  font-size: 12px;
                  font-weight: 500;
                "></span>
                  Total Monthly Charges:&nbsp;<span style="font-weight: 700; font-size: 14px; font-family: 'sofia-pro', sans-serif;">${netRents}k€</span>
              </div>
              <div style="
                display: flex;
                align-items: center;
                line-height: 24px;
                color: #212529;
                font-family: 'sofia-pro', sans-serif;
              ">
                <span style="
                  width: 12px;
                  height: 12px;
                  background: #3C6895;
                  border-radius: 50%;
                  display: inline-block;
                  margin-right: 12px;
                  flex-shrink: 0;
                  font-size: 12px;
                  font-weight: 500;
                "></span>
                  Total net rents:&nbsp;<span style="font-weight: 700; font-size: 14px; font-family: 'sofia-pro', sans-serif;">${charges}k€</span>
              </div>
        `;
      },
    },
    grid: {
      borderColor: '#E9ECEF',
      strokeDashArray: 4,
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
      padding: {
        top: 0,
        right: 20,
        bottom: 0,
        left: 0,
      },
    },
  };

  const series = [
    {
      name: 'Total net rents',
      data: data.rent.map((val) => Number((val / 1000).toFixed(1))),
    },
    {
      name: 'Total Monthly Charges',
      data: data.charges.map((val) => Number((val / 1000).toFixed(1))),
    },
  ];

  return (
    <Box
      className="rounded-[16px] bg-white p-4 shadow-small"
      sx={{
        '& .apexcharts-tooltip': {
          border: 'none',
          width: '280px',
          borderRadius: '16px',
          padding: '24px',
          background: 'white',
          fontFamily: 'sofia-pro',
          gap: '8px',
        },
        '& .apexcharts-theme-light': {
          boxShadow: '0px 8px 24px 0px #71829126',
        },
        '& .apexcharts-tooltip.apexcharts-theme-light': {
          background: '#FFFFFF',
          border: 'none',
        },
        '& .apexcharts-legend': {
          padding: '0 20px',
        },
        '& .apexcharts-series .apexcharts-bar-area': {
          opacity: '1',
        },
        '& .apexcharts-series:has(~ .apexcharts-series .apexcharts-bar-area:hover) .apexcharts-bar-area, & .apexcharts-series:has(.apexcharts-bar-area:hover) ~ .apexcharts-series .apexcharts-bar-area, & .apexcharts-series:has(.apexcharts-bar-area:hover) .apexcharts-bar-area:not(:hover)':
          {
            opacity: '0.3',
          },
        '& .apexcharts-bar-area:hover': {
          opacity: '1 !important',
          stroke: '#000000',
          strokeWidth: '2px',
        },
        '& .apexcharts-datalabel': {
          fill: '#FFFFFF !important',
        },
        '& .apexcharts-datalabel-label': {
          fill: '#212529 !important',
        },
      }}
    >
      <Typography
        variant="h6"
        className="mb-3 font-Vollkorn text-lead1 font-bold leading-[27px] text-[#111827]"
      >
        {t('js.features.admin.stats.dashboard.financial_chart.title')}
      </Typography>
      {/* Custom Legend */}
      <Box className="flex justify-end gap-4">
        {[
          { label: 'Total Monthly Charges', color: '#1B998B' },
          { label: 'Total net rents', color: '#3C6895' },
        ].map((item) => (
          <Box key={item.label} className="flex items-center">
            <Box
              sx={{ width: 12, height: 12 }}
              className="mr-1 rounded-full"
              style={{ backgroundColor: item.color }}
            />
            <Typography
              variant="lead1"
              className="vacancy-maintenance-overview text-footnote1 font-medium text-[#212529]"
              sx={{
                '&.pdf-export': {
                  marginTop: '-12px',
                },
              }}
            >
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>
      <ReactApexChart options={options} series={series} type="bar" height={350} />
    </Box>
  );
};
